import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  CharacterCreate,
  CharacterRead,
  CharacterUpdate,
  type CharacterCreate as CharacterCreateT,
  type CharacterUpdate as CharacterUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const CharacterList = z.array(CharacterRead);

export function useCharacters(projectId: number) {
  return useQuery({
    queryKey: qk.characters(projectId),
    queryFn: () => api.get(`/api/projects/${projectId}/characters`, CharacterList),
    enabled: projectId > 0,
  });
}

export function useCreateCharacter(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CharacterCreateT) =>
      api.post(
        `/api/projects/${projectId}/characters`,
        CharacterCreate.parse(input),
        CharacterRead,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.characters(projectId) });
    },
  });
}

export function useUpdateCharacter(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: CharacterUpdateT }) =>
      api.patch(`/api/characters/${id}`, CharacterUpdate.parse(patch), CharacterRead),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.characters(projectId) });
    },
  });
}

export function useDeleteCharacter(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/characters/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.characters(projectId) });
    },
  });
}
